import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { uploadImage } from '../../services/uploadsApi';

// Multiple-image uploader. `value` is an array of URLs.
// onChange(nextArray) fires when uploads succeed or images are removed.
const MultiImageUploader = ({ value, onChange, folder = 'uploads', label = 'Images' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const urls = Array.isArray(value) ? value : [];

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length !== files.length) {
      setError('Some files were skipped (not images)');
    } else {
      setError('');
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of images) {
        const url = await uploadImage(f, folder);
        uploaded.push(url);
      }
      onChange([...urls, ...uploaded]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (i) => {
    const next = urls.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>{label}</Typography>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {urls.map((url, i) => (
          <Box
            key={i}
            sx={{
              position: 'relative', width: 80, height: 80,
              borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider',
            }}
          >
            <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <IconButton
              size="small"
              onClick={() => removeAt(i)}
              sx={{
                position: 'absolute', top: 2, right: 2,
                bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', p: 0.25,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          onClick={handlePick}
          disabled={uploading}
          sx={{ width: 80, height: 80, minWidth: 0, flexDirection: 'column', gap: 0.5, p: 0.5, fontSize: '0.65rem' }}
        >
          {uploading ? <CircularProgress size={20} /> : <CloudUploadRounded />}
          {uploading ? 'Uploading' : 'Add'}
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>{error}</Typography>
      )}
    </Box>
  );
};

export default MultiImageUploader;
